import { Link } from 'react-router-dom';
import {
  BUCKET_LABELS,
  BUCKET_ORDER,
  bucketFor,
  formatDurationSeconds,
  formatExact,
  formatRelative,
  type Bucket,
} from '../lib/feedTime';

export interface FeedParticipant {
  userId: string;
  name: string | null;
  email: string;
  role: string;
  joinedAt: string;
}

export interface FeedItem {
  id: string;
  createdAt: string;
  status: string;
  initiator: { id: string; name: string | null; email: string };
  personaName: string;
  goalLabel: string | null;
  timeLimitMinutes: number;
  startedAt: string | null;
  endedAt: string | null;
  durationSeconds: number | null;
  participants: FeedParticipant[];
  org?: { id: string; name: string };
}

interface RecentActivityFeedProps {
  items: FeedItem[];
  showOrg?: boolean;
  onLoadMore?: () => void;
  hasMore?: boolean;
  loading?: boolean;
  emptyLabel?: string;
}

function displayName(p: { name: string | null; email: string }): string {
  return p.name?.trim() || p.email;
}

function statusClass(status: string): string {
  if (status === 'completed') return 'bg-gray-100 text-gray-700';
  if (status === 'active') return 'bg-green-100 text-green-800';
  return 'bg-slate-100 text-slate-700';
}

function ParticipantsList({ participants }: { participants: FeedParticipant[] }) {
  if (participants.length === 0) return null;
  const shown = participants.slice(0, 3);
  const extra = participants.length - shown.length;
  return (
    <span className="text-xs text-gray-500">
      {' with '}
      {shown.map((p, idx) => (
        <span key={p.userId}>
          {idx > 0 && ', '}
          <span title={`${displayName(p)} · ${p.role}`}>{displayName(p)}</span>
        </span>
      ))}
      {extra > 0 && <span> +{extra}</span>}
    </span>
  );
}

function FeedRow({ item, showOrg }: { item: FeedItem; showOrg?: boolean }) {
  const createdIso =
    typeof item.createdAt === 'string' ? item.createdAt : new Date(item.createdAt).toISOString();
  return (
    <Link
      to={`/practice/${item.id}`}
      className="block px-4 py-3 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-600"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm text-gray-900">
            <span className="font-medium">{displayName(item.initiator)}</span>
            <span className="text-gray-600"> started a practice session</span>
            {item.personaName && (
              <span className="text-gray-600">
                {' with '}
                <span className="font-medium text-gray-800">{item.personaName}</span>
              </span>
            )}
            {item.goalLabel && (
              <span className="text-gray-600">
                {' · goal: '}
                <span className="text-gray-800">{item.goalLabel}</span>
              </span>
            )}
            <ParticipantsList participants={item.participants} />
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
            {showOrg && item.org && (
              <span className="font-medium text-gray-700">{item.org.name}</span>
            )}
            <span
              className={`inline-flex px-2 py-0.5 rounded-full text-xs ${statusClass(item.status)}`}
            >
              {item.status}
            </span>
            <span>
              <span className="text-gray-400">planned </span>
              {`${item.timeLimitMinutes}m`}
            </span>
            <span>
              <span className="text-gray-400">actual </span>
              {formatDurationSeconds(item.durationSeconds)}
            </span>
          </div>
        </div>
        <time
          dateTime={createdIso}
          title={formatExact(item.createdAt)}
          className="shrink-0 text-xs text-gray-400"
        >
          {formatRelative(item.createdAt)}
        </time>
      </div>
    </Link>
  );
}

export function RecentActivityFeed({
  items,
  showOrg,
  onLoadMore,
  hasMore,
  loading,
  emptyLabel = 'No activity yet.',
}: RecentActivityFeedProps) {
  if (!loading && items.length === 0) {
    return <div className="p-8 text-center text-sm text-gray-500">{emptyLabel}</div>;
  }

  const grouped: Record<Bucket, FeedItem[]> = {
    today: [],
    thisWeek: [],
    lastWeek: [],
    older: [],
  };
  const now = new Date();
  for (const item of items) {
    grouped[bucketFor(item.createdAt, now)].push(item);
  }

  return (
    <div>
      <div className="divide-y">
        {BUCKET_ORDER.filter((b) => grouped[b].length > 0).map((bucket) => (
          <section key={bucket} aria-label={BUCKET_LABELS[bucket]}>
            <h3 className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500 bg-gray-50 border-b">
              {BUCKET_LABELS[bucket]}
            </h3>
            <ul className="divide-y">
              {grouped[bucket].map((item) => (
                <li key={item.id}>
                  <FeedRow item={item} showOrg={showOrg} />
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
      {hasMore && onLoadMore && (
        <div className="p-3 border-t text-center">
          <button
            type="button"
            onClick={onLoadMore}
            disabled={loading}
            className="text-sm font-medium text-green-700 hover:text-green-800 disabled:opacity-50"
          >
            {loading ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </div>
  );
}
